/**
 * Adds translation keys to Lokalise the same way admin-master does in local dev (src/i18n/index.ts there):
 * a PUT to the lokalise-webhook Cloud Function, which holds the Lokalise token. No credentials live here.
 * Existence is checked against the public translations CDN, which only reflects the last Lokalise Build.
 */

/** Namespace admin-master reads request type template names and request subtype translation keys from. */
export const REQUESTS_NAMESPACE = 'requests-page-tmp';

const LOKALISE_WEBHOOK_URL = 'https://us-central1-oitchau-155812.cloudfunctions.net/lokalise-webhook';
const TRANSLATIONS_URL = 'https://admin.oitchau.com.br/static/locales';
const BASE_LANGUAGE = 'en';

type Translations = Record<string, string>;

const namespaceCache = new Map<string, Promise<Translations>>();
const sentKeys = new Set<string>();

const sentKeyId = (namespace: string, key: string) => `${namespace}|${key}`;

/** Lokalise keys must not have leading/trailing spaces (admin-master README) and can't contain the i18next namespace separator. */
export const isValidTranslationKey = (key: string): boolean => key.length > 0 && key === key.trim() && !key.includes('|');

export const buildLokalisePayload = (namespace: string, key: string) => ({ [namespace]: { [key]: key } });

export const fetchNamespaceTranslations = (namespace: string): Promise<Translations> => {
  let cached = namespaceCache.get(namespace);
  if (!cached) {
    cached = fetch(`${TRANSLATIONS_URL}/${BASE_LANGUAGE}/${namespace}`).then((res) => {
      if (!res.ok) throw new Error(`Failed to load translations for ${namespace}: ${res.status}`);
      return res.json();
    });
    // Drop failed loads so the next check retries instead of reusing the rejection.
    cached.catch(() => namespaceCache.delete(namespace));
    namespaceCache.set(namespace, cached);
  }
  return cached;
};

export const wasSentToLokalise = (namespace: string, key: string): boolean => sentKeys.has(sentKeyId(namespace, key));

/**
 * The webhook only accepts origins on its CORS allowlist. From any other origin fetch rejects with a TypeError
 * and the key is not added, so a blocked response is reported as a failure, never as sent.
 */
export const addKeyToLokalise = async (namespace: string, key: string): Promise<void> => {
  if (!isValidTranslationKey(key)) throw new Error(`Invalid translation key: "${key}"`);
  let res: Response;
  try {
    res = await fetch(LOKALISE_WEBHOOK_URL, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildLokalisePayload(namespace, key))
    });
  } catch (e) {
    if (!(e instanceof TypeError)) throw e;
    throw new Error(`Couldn't reach the Lokalise webhook – is ${window.location.origin} on its CORS allowlist?`);
  }
  if (!res.ok) throw new Error(`Lokalise webhook responded with ${res.status}`);
  sentKeys.add(sentKeyId(namespace, key));
};

/** Test-only: forget cached namespaces and sent keys. */
export const resetLokaliseCache = () => {
  namespaceCache.clear();
  sentKeys.clear();
};
